import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import {debounceTime, from, fromEvent} from 'rxjs';
import {getConversationByFilter} from '../../services/api/messaging';
import {map} from 'rxjs/operators';
import _debounce from 'lodash/debounce';
import {CONVERSATION_RELOAD} from '../../services/events';
import {getTextByKey} from "../../services/constants/ConversationFilterType";
import ConversationItem from "../../components/Item/ConversationItem";
import LoadSpinner from "../../components/Common/LoadSpinner";

const ConversationList =
    memo(function ConversationList({
                                       authData,
                                       activeConversation,
                                       activeFilterOptions,
                                       onSelectConversation,
                                       onHandleConversationArchived
                                   }) {
        const [conversationPage, setConversationPage] = useState(1);
        const [loadedConversationPage, setLoadedConversationPage] = useState(0);
        const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
        const hasLoaded = useRef(false);
        const [conversationData, setConversationData] = useState([]);
        const [conversationMeta, setConversationMeta] = useState({});

        const conversationsRef = useRef(null);

        const handleConversationScroll = _debounce(() => {
            const {scrollTop, scrollHeight, clientHeight} = conversationsRef.current;
            if (scrollTop + clientHeight >= scrollHeight - 200) {
                const totalPages = conversationMeta?.pagination?.total_pages || 0;
                if (conversationPage < totalPages && !loadingMoreConversations) {
                    setLoadingMoreConversations(true);
                    setConversationPage((prevPage) => prevPage + 1);
                }
            }
        }, 250);

        const handleSelectedConversation = useCallback((conversation, skipRead) => {
            onSelectConversation(conversation, skipRead);
        }, [onSelectConversation]);

        const fetchOwnConversations = useCallback((filter) => {
            return from(getConversationByFilter(filter))
                .pipe(
                    map((response) => response)
                )
                .subscribe(
                    (result) => {
                        setLoadingMoreConversations(false);
                        let metaData = result.meta;
                        let pagination = metaData.pagination;

                        if (activeConversation === null && result.data.length > 0) {
                            handleSelectedConversation(result.data[0]);
                        }

                        if (pagination.current_page > 1) {
                            setConversationData((prevMessages) => [...prevMessages, ...result.data]);
                        } else {
                            setConversationData(result.data);
                        }
                        setConversationMeta(result.meta);
                        setLoadedConversationPage(pagination.current_page);

                        if (activeConversation) {
                            let activeOne = result.data.find(conversation => conversation.id === activeConversation.id);
                            handleSelectedConversation(activeOne);
                        }
                    },
                    (error) => {
                        console.error('Error fetching messages:', error);
                    }
                );
        }, [activeConversation, handleSelectedConversation]);

        useEffect(() => {
                if (activeConversation && hasLoaded.current !== activeConversation.id) {
                    handleSelectedConversation(activeConversation, false);
                    hasLoaded.current = activeConversation.id;
                }
            },
            /* eslint-disable react-hooks/exhaustive-deps */
            [activeConversation]);

        useEffect(() => {
            if (activeConversation) {
                handleSelectedConversation(activeConversation);
            }

            if (activeFilterOptions.page === 0) {
                setLoadedConversationPage(0);
                setConversationPage(1);
                setConversationData([]);
                setConversationMeta([]);
                setLoadingMoreConversations(true);
            }

            if (conversationPage > loadedConversationPage) {
                activeFilterOptions.page = conversationPage;
                fetchOwnConversations(activeFilterOptions);
            }

            const conversationChangeObservable = fromEvent(document, 'conversation_scroll');
            const debouncedConversationChangeObservable = conversationChangeObservable.pipe(debounceTime(500));

            const subscription = debouncedConversationChangeObservable.subscribe(() => {
                if (conversationPage <= loadedConversationPage && loadedConversationPage > 0) {
                    return;
                }
                if (conversationMeta.pagination && conversationMeta.pagination.total_pages === conversationMeta.pagination.current_page) {
                    return;
                }

                setLoadedConversationPage((prevPage) => prevPage + 1);
            });

            if (conversationsRef.current) {
                conversationsRef.current.addEventListener('scroll', handleConversationScroll);
            }

            return () => {
                subscription.unsubscribe();
                if (conversationsRef.current) {
                    // eslint-disable-next-line react-hooks/exhaustive-deps
                    conversationsRef.current.removeEventListener('scroll', handleConversationScroll);
                }
            };
        }, [activeConversation, activeFilterOptions, conversationMeta.pagination, conversationPage, fetchOwnConversations, handleConversationScroll, handleSelectedConversation, loadedConversationPage, onSelectConversation]);

        useEffect(() => {
            const reloadConversationEvent = () => {
                setLoadedConversationPage(0);
                setConversationPage(1);
            };

            document.addEventListener(CONVERSATION_RELOAD, reloadConversationEvent);
            return () => {
                document.removeEventListener(CONVERSATION_RELOAD, reloadConversationEvent);
            };
        }, []);

        return (
            <div
                className="sl-flex sl-grow-1 sl-flex-col sl-gap-0 sl-justify-flex-start sl-align-flex-start sl-h-full sl-w-full sl-pr-1">
                <div className="sl-grow-1 sl-w-full sl-customized-scrollbar sl-overflow-y-scroll sl-border-radius-0"
                     ref={conversationsRef}
                     onScroll={handleConversationScroll}>
                    <div className="sl-m-0 sl-p-0 sl-w-full">
                        {conversationData.map((conversation, index) => (
                            <ConversationItem key={index} conversation={conversation}
                                              activeConversation={activeConversation}
                                              authData={authData}
                                              onSelectConversation={handleSelectedConversation}
                                              onHandleConversationArchived={onHandleConversationArchived}
                            />
                        ))}
                    </div>

                    {(conversationMeta.pagination && conversationMeta.pagination['count'] === 0) && (
                        <div className="sl-conversations sl-mt-12 sl-w-full sl-flex sl-align-center sl-justify-center">
                            <div
                                className="sl-font-medium sl-text-lg sl-text-gray-700">No {getTextByKey(activeFilterOptions.status)}</div>
                        </div>
                    )}

                    {loadingMoreConversations && <div className="sl-my-12 sl-flex"><LoadSpinner/></div>}
                </div>
            </div>
        );
    });

export default ConversationList;
